import { CSSProperties } from 'react';
import clsx from 'clsx';
import { makeStyles, Paper, Tooltip, SvgIconTypeMap } from '@material-ui/core';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import CheckIcon from '@material-ui/icons/Check';
import ArchiveIcon from '@material-ui/icons/Archive';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import MoreHorizRoundedIcon from '@material-ui/icons/MoreHorizRounded';
import PersonRoundedIcon from '@material-ui/icons/PersonRounded';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import PersonIcon from '@material-ui/icons/Person';
import LinkIcon from '@material-ui/icons/Link';
import PublicIcon from '@material-ui/icons/Public';
import { Colors } from '../controls/helpers';
import { AdviceDelivery, OnboardDelivery } from '../../api';

export const NotStartedChip = ({ title, customStyle, toolTipText }: { title: string; customStyle?: CSSProperties; toolTipText?: string }) => {
    const classes = useStyles();
    return <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{toolTipText || 'Not started'}</span>}>
        <Paper elevation={0} className={classes.chip} style={{ backgroundColor: Colors.Grey, ...customStyle }}>
            <span>{title}</span>
            <MoreHorizRoundedIcon fontSize="small" className={classes.roundIcon} style={{ color: Colors.Grey, border: `1px solid ${Colors.Grey}` }} />
        </Paper>
    </Tooltip>
}

export const InProgressChip = ({ title, customStyle, toolTipText }: { title: string; customStyle?: CSSProperties; toolTipText?: string }) => {
    const classes = useStyles();
    return <Tooltip title={toolTipText || 'In progress'}>
        <Paper elevation={0} className={classes.chip} style={{ backgroundColor: Colors.Orange, ...customStyle }}>
            <span>{title}</span>
            <MoreHorizRoundedIcon fontSize="small" className={classes.roundIcon} style={{ color: Colors.Orange, border: `1px solid ${Colors.Orange}` }} />
        </Paper>
    </Tooltip>
}

export const FailedChip = ({ title, customStyle, toolTipText }: { title: string; customStyle?: CSSProperties; toolTipText?: string }) => {
    const classes = useStyles();
    return <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{toolTipText || 'Action required'}</span>}>
        <Paper elevation={0} className={classes.chip} style={{ backgroundColor: Colors.Red, ...customStyle }}>
            <span>{title}</span>
            <ErrorOutlineIcon fontSize="small" />
        </Paper>
    </Tooltip>
}

export const CompletedChip = ({ title, customStyle, toolTipText }: { title: string; customStyle?: CSSProperties; toolTipText?: string }) => {
    const classes = useStyles();
    return <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{toolTipText || 'Completed'}</span>}>
        <Paper elevation={0} className={classes.chip} style={{ backgroundColor: Colors.Green, ...customStyle }}>
            <span>{title}</span>
            <CheckIcon fontSize="small" />
        </Paper>
    </Tooltip>
}

export const ApplicantRoleChip = ({ title }: { title: string }) => {
    const classes = useStyles();
    return <Paper elevation={0} className={clsx(classes.chip, classes.applicantRoleChip)}>
        <span>{title}</span>
        <PersonRoundedIcon fontSize="small" className={classes.roundIcon} style={{ color: Colors.DarkGrey, border: `1px solid ${Colors.DarkGrey}` }} />
    </Paper>
}

export const ArchiveChip = ({ title }: { title: string }) => {
    const classes = useStyles();
    return <Paper elevation={0} className={classes.chip} color={Colors.DarkGrey}>
        <span>{title}</span>
        <ArchiveIcon fontSize="small" />
    </Paper>
}

enum InitiationType {
    App,
    Port,
    Referral,
    Browser
}

const initiationTypeDetails: Record<InitiationType, { tooltip: string; Icon: OverridableComponent<SvgIconTypeMap<{},'svg'>>; }> = {
    [InitiationType.App]: { tooltip: 'App initiated', Icon: PhoneIphoneIcon },
    [InitiationType.Port]: { tooltip: 'Port initiated', Icon: PersonIcon },
    [InitiationType.Referral]: { tooltip: 'Referral link initiated', Icon: LinkIcon },
    [InitiationType.Browser]: { tooltip: 'Website initiated', Icon: PublicIcon },
};

export const SourceIcon = ({ adviceDelivery, referralCode, onboardDelivery }: { adviceDelivery: AdviceDelivery | undefined; referralCode: string | undefined; onboardDelivery: OnboardDelivery | undefined; }) => {
    const initiationType = onboardDelivery === OnboardDelivery.Browser && !adviceDelivery ? InitiationType.Browser : referralCode ? InitiationType.Referral : onboardDelivery === OnboardDelivery.App && !adviceDelivery ? InitiationType.App : InitiationType.Port;
    const { tooltip, Icon } = initiationTypeDetails[initiationType];  

    return <Tooltip title={tooltip}>
        <Icon style={{ color: Colors.Blue, marginTop: 5 }} />
    </Tooltip>
}

export const ArchivesIcon = ({ isArchive }: { isArchive: boolean }) => {
    const Icon = isArchive ? ArchiveIcon : UnarchiveIcon;
    return <Tooltip title={isArchive ? 'Archive' : 'Unarchive'}>
        <Icon style={{ flexGrow: 1, color: Colors.Blue }} />
    </Tooltip>
}

const useStyles = makeStyles({
    chip: {
        display: 'inline-flex',
        padding: '3px 7px 3px 11px',
        marginRight: 3,
        height: 28,
        fontSize: '0.875rem',
        fontWeight: 500,
        color: Colors.White,
        borderRadius: '0.5rem',
        backgroundColor: Colors.Grey,
        '& span': {
            paddingRight: 4
        },
        '& svg': {
            marginTop: 1
        },
        '&:last-child': {
            marginRight: 0
        }
    },
    applicantRoleChip: {
        borderRadius: '1.5rem',
        backgroundColor: Colors.DarkGrey
    },
    roundIcon: {
        borderRadius: '50%',
        backgroundColor: Colors.White
    }
}, { name: 'IconsAndChips' });
